import React from "react";

import { useFormikContext } from "formik";
import PropTypes from "prop-types";

import { CheckboxWithUnorderedList } from "shared/components/Form";
import Icon from "shared/components/Icon";
import Settings from "shared/components/Settings";
import Tooltip from "shared/components/Tooltip";
import { RegularText } from "shared/components/Typography";
import Well from "shared/components/Well";
import { usePermissions } from "shared/hooks";
import { conditionalItem } from "shared/utils/array";
import { useBgccEntitlement } from "manager/hooks/api/useBgccEntitlement";

import ApplicationSetupHeader from "../Header";
import ResidenceEmploymentDuration, {
  DEFAULT_DURATION_TYPE,
  DEFAULT_DURATION_VALUE,
} from "../ResidenceEmploymentDuration";
import SentilinkIdentificationToggle from "../SentilinkIdentificationToggle";
import { LabelIconContainer, StyledSkeleton as Skeleton } from "../styled";
import {
  getAllowManagersToViewSSNItem,
  getAssetsVerificationItem,
  getBlockApplicationSubmitIfNoIncomeItem,
  getForceIncomeDocumentsItem,
  useGuardedForceDocsToggle,
  useHasIncomeDocsSurfaceByPersona,
} from "../utils";

const guarantorPersonaIdentification = "requiresGuarantorPersonaIdentification";
const guarantorSentilinkIdentification =
  "requiresGuarantorSentilinkIdentification";
const guarantorArgyleVerification = "requiresGuarantorArgyleVerification";
const renterProfileGuarantors = "requiresRenterProfileGuarantor";

/** Income & Assets */
const incomeAssetVerification = "requiresIncomeAssetGuarantors";
const assetVerificationWhenLinkedEmployment =
  "requiresAssetVerifLinkedEmploymentGuarantors";
const blockApplicationSubmitIfNoIncome =
  "blockDealSubmitIfIncomeDataIsMissingGuarantors";
const forceGuarantorIncomeDocuments = "forceGuarantorIncomeDocuments";

/** Background and credit check */
const backgroundCreditCheckGuarantors =
  "requiresBackgroundCreditCheckGuarantors";
const allowManagerViewSSN = "allowManagerViewSsnGuarantor";

/** Residential History Guarantor */
const residentialHistoryDurationFlag =
  "requiresResidentialHistoryDurationGuarantor";
const residentialHistoryDurationType = "residentialHistoryRangeGuarantor";
const residentialHistoryDurationValue = "residentialHistoryDurationGuarantor";

/** Employment History Guarantor */
const employmentHistoryDurationFlag =
  "requiresEmploymentHistoryDurationGuarantor";
const employmentHistoryDurationType = "employmentHistoryRangeGuarantor";
const employmentHistoryDurationValue = "employmentHistoryDurationGuarantor";

const ScreeningForGuarantors = ({
  loading,
  disabled,
  isVeroGoActive,
  dealSetup,
}) => {
  const { values, setFieldValue, setValues } = useFormikContext();
  const {
    isArgyleEnabled,
    isPersonaEnabled,
    isIdFraudRiskEnabled: isSentilinkEnabled,
  } = usePermissions();

  const { data: bgccEntitlement, isLoading: isBgccEntitlementLoading } = useBgccEntitlement();
  const isBgccEnabled = bgccEntitlement?.bgccEnabled ?? true;
  const bgccDisabled = disabled || !isBgccEnabled;

  const isLoading = loading || isBgccEntitlementLoading;

  const isV1ExpressionEngine = Boolean(dealSetup?.isV1ExpressionEngine);
  const incomeDocsByPersona = useHasIncomeDocsSurfaceByPersona();
  const guarantorHasDocSurface = incomeDocsByPersona.guarantor;
  const guarantorForceIncomeDocumentsItem = getForceIncomeDocumentsItem({
    values,
    prop: forceGuarantorIncomeDocuments,
    isGuarantor: true,
    disabled,
  });
  const onGuardedForceDocsToggle = useGuardedForceDocsToggle({
    hasDocSurface: guarantorHasDocSurface,
  });
  const guarantorLinkEmploymentOn = Boolean(
    values?.[guarantorArgyleVerification]
  );
  const guarantorIncomeAndBalancesOn = Boolean(
    values?.[incomeAssetVerification]
  );
  const showForceDocsInGuarantorIncomeAndBalances =
    isV1ExpressionEngine && guarantorIncomeAndBalancesOn;
  const showForceDocsInGuarantorLinkEmployment =
    isV1ExpressionEngine &&
    guarantorLinkEmploymentOn &&
    !guarantorIncomeAndBalancesOn;
  const guarantorForceDocsChildItem = {
    label: "Require Document Uploads",
    id: "force-income-documents-guarantor",
    disabled,
    children: [guarantorForceIncomeDocumentsItem],
  };

  return (
    <Settings
      header={
        <Skeleton loading={isLoading}>
          <ApplicationSetupHeader
            title="Screening for guarantors"
            subTitle="Please select screening level you would like to require from guarantors"
          />
        </Skeleton>
      }
      nodes={[
        ...conditionalItem(isSentilinkEnabled, {
          key: 5,
          component: (
            <SentilinkIdentificationToggle
              id="guarantor-sentilink-identification"
              field={guarantorSentilinkIdentification}
              loading={loading}
              disabled={disabled}
            />
          ),
        }),
        ...conditionalItem(isPersonaEnabled, {
          key: 10,
          component: (
            <Skeleton loading={loading}>
              <CheckboxWithUnorderedList
                id="guarantor-persona-identification"
                label={
                  isVeroGoActive ? (
                    <LabelIconContainer alignItems="center">
                      <RegularText>Identity Verification</RegularText>
                      <Tooltip
                        title={
                          <Well noBorder>
                            This configuration is required for Vero Go.
                          </Well>
                        }
                      >
                        <Icon.InfoIcon className="extra-info" />
                      </Tooltip>
                    </LabelIconContainer>
                  ) : (
                    "Identity Verification"
                  )
                }
                onChange={(checked) => {
                  setFieldValue(guarantorPersonaIdentification, checked);
                }}
                value={values[guarantorPersonaIdentification]}
                disabled={disabled || isVeroGoActive}
              />
            </Skeleton>
          ),
        }),
        ...conditionalItem(isArgyleEnabled && !isVeroGoActive, {
          key: 15,
          component: (
            <Skeleton loading={loading}>
              <CheckboxWithUnorderedList
                id="guarantor-link-employment"
                label="Link Employment"
                onChange={(checked) => {
                  setFieldValue(guarantorArgyleVerification, checked);
                  if (checked === false && !guarantorIncomeAndBalancesOn) {
                    setFieldValue(forceGuarantorIncomeDocuments, false);
                  }
                }}
                value={values[guarantorArgyleVerification]}
                items={[
                  ...conditionalItem(
                    showForceDocsInGuarantorLinkEmployment,
                    guarantorForceDocsChildItem
                  ),
                ]}
                onItemChildChange={(id, value) => {
                  if (id === forceGuarantorIncomeDocuments) {
                    onGuardedForceDocsToggle(forceGuarantorIncomeDocuments, value);
                    return;
                  }
                  setFieldValue(id, value);
                }}
                disabled={disabled}
              />
            </Skeleton>
          ),
        }),
        ...conditionalItem(!isVeroGoActive, {
          key: 20,
          component: (
            <Skeleton loading={loading}>
              <CheckboxWithUnorderedList
                id="income-asset-verification-for-guarantors"
                label="Income & Balances Verification"
                value={values[incomeAssetVerification]}
                items={[
                  { label: "Income", id: "income-for-guarantors" },
                  getAssetsVerificationItem(
                    isArgyleEnabled,
                    values,
                    assetVerificationWhenLinkedEmployment,
                    disabled
                  ),
                  {
                    label: "Block Applications Without Verified Income",
                    id: "block-application-submission-if-no-income-guarantors",
                    disabled,
                    children: [
                      getBlockApplicationSubmitIfNoIncomeItem(
                        values,
                        blockApplicationSubmitIfNoIncome,
                        true
                      ),
                    ],
                  },
                  ...conditionalItem(
                    showForceDocsInGuarantorIncomeAndBalances,
                    guarantorForceDocsChildItem
                  ),
                ]}
                onChange={(value) => {
                  setFieldValue(incomeAssetVerification, value);
                  if (value === false && !guarantorLinkEmploymentOn) {
                    setFieldValue(forceGuarantorIncomeDocuments, false);
                  }
                }}
                onItemChildChange={(id, value) => {
                  if (id === forceGuarantorIncomeDocuments) {
                    onGuardedForceDocsToggle(forceGuarantorIncomeDocuments, value);
                    return;
                  }
                  setFieldValue(id, value);
                }}
                disabled={disabled}
              />
            </Skeleton>
          ),
        }),
        {
          key: 30,
          component: (
            <Skeleton loading={loading}>
              <CheckboxWithUnorderedList
                id="background-credit-check-for-guarantors"
                label={
                  !isBgccEnabled ? (
                    <LabelIconContainer alignItems="center">
                      <RegularText>Background &amp; Credit check</RegularText>
                      <Tooltip
                        title={
                          <Well noBorder>
                            Background & Credit Check features are not available on your company's plan.
                            Please contact your VERO Customer Success Manager to activate this feature.
                          </Well>
                        }
                      >
                        <Icon.InfoIcon className="extra-info" />
                      </Tooltip>
                    </LabelIconContainer>
                  ) : isVeroGoActive && !values[backgroundCreditCheckGuarantors] ? (
                    <LabelIconContainer alignItems="center">
                      <RegularText>Background &amp; Credit check</RegularText>
                      <Tooltip
                        title={
                          <Well noBorder>
                            Background &amp; Credit Check is turned off by default when Vero Go is active.
                            Contact your VERO Customer Success Manager to enable it.
                          </Well>
                        }
                      >
                        <Icon.InfoIcon className="extra-info" />
                      </Tooltip>
                    </LabelIconContainer>
                  ) : (
                    "Background & Credit check"
                  )
                }
                value={values[backgroundCreditCheckGuarantors]}
                disabled={bgccDisabled}
                onChange={(checked) => {
                  setFieldValue(backgroundCreditCheckGuarantors, checked);
                }}
                onItemChildChange={(id, value) => {
                  setFieldValue(id, value);
                }}
                items={[
                  { label: "Credit Check", id: "credit-check-for-guarantors" },
                  {
                    label: "Eviction Related Proceedings History",
                    id: "housing-court-records-history-guarantors",
                  },
                  {
                    label: "Background Check",
                    id: "background-check-for-guarantors",
                    disabled: bgccDisabled,
                    children: [
                      getAllowManagersToViewSSNItem(
                        allowManagerViewSSN,
                        values
                      ),
                    ],
                  },
                ]}
              />
            </Skeleton>
          ),
        },
        {
          key: 40,
          component: (
            <Skeleton loading={loading}>
              <CheckboxWithUnorderedList
                id="renter-profile-for-guarantors"
                label="Renter Profile"
                disabled={disabled}
                items={[
                  {
                    id: "residential-history-duration-guarantors",
                    label: "Residential History",
                    disabled,
                    children: [
                      {
                        id: residentialHistoryDurationFlag,
                        label: "Set Minimum History",
                        checked: values[residentialHistoryDurationFlag],
                        checkedContent: (
                          <ResidenceEmploymentDuration
                            durationTypeFieldName={
                              residentialHistoryDurationType
                            }
                            durationValueFieldName={
                              residentialHistoryDurationValue
                            }
                            disabled={disabled}
                          />
                        ),
                      },
                    ],
                  },
                  {
                    id: "employment-history-duration-guarantors",
                    label: "Employment History",
                    disabled,
                    children: [
                      {
                        id: employmentHistoryDurationFlag,
                        label: "Set Minimum History",
                        checked: values[employmentHistoryDurationFlag],
                        checkedContent: (
                          <ResidenceEmploymentDuration
                            durationTypeFieldName={
                              employmentHistoryDurationType
                            }
                            durationValueFieldName={
                              employmentHistoryDurationValue
                            }
                            disabled={disabled}
                          />
                        ),
                      },
                    ],
                  },
                  {
                    label: "Lease Preferences (optional)",
                    id: "lease-preferences",
                  },
                  {
                    label: "Additional Information",
                    id: "additional-information-for-guarantors",
                  },
                ]}
                value={values[renterProfileGuarantors]}
                onChange={(value) => {
                  setFieldValue(renterProfileGuarantors, value);
                }}
                onItemChildChange={(id, value) => {
                  if (id === residentialHistoryDurationFlag) {
                    setValues({
                      ...values,
                      [residentialHistoryDurationFlag]: value,
                      [residentialHistoryDurationType]: DEFAULT_DURATION_TYPE,
                      [residentialHistoryDurationValue]: DEFAULT_DURATION_VALUE,
                    });
                  } else if (id === employmentHistoryDurationFlag) {
                    setValues({
                      ...values,
                      [employmentHistoryDurationFlag]: value,
                      [employmentHistoryDurationType]: DEFAULT_DURATION_TYPE,
                      [employmentHistoryDurationValue]: DEFAULT_DURATION_VALUE,
                    });
                  } else {
                    setFieldValue(id, value);
                  }
                }}
              />
            </Skeleton>
          ),
        },
      ]}
    />
  );
};

ScreeningForGuarantors.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  isVeroGoActive: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  dealSetup: PropTypes.object,
};

ScreeningForGuarantors.defaultProps = {
  disabled: false,
  loading: false,
  isVeroGoActive: false,
  dealSetup: null,
};

export default ScreeningForGuarantors;
